import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { CheckCircle2, ChevronDown, ChevronUp, Info } from "lucide-react";

interface Props {
  /** Show the panel expanded by default (e.g. right after connecting Zoom). */
  defaultOpen?: boolean;
}

const ZOOM_AI_COMPANION_URL =
  "https://support.zoom.com/hc/en/article?id=zm_kb&sysparm_article=KB0064927";
const ZOOM_AI_ADMIN_URL =
  "https://zoom.us/account/setting?tab=ai_companion";

export default function ZoomAICompanionRequirements({ defaultOpen = false }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);

  const requirements: string[] = t(
    "scheduler.settings.zoomAI.requirements",
    { returnObjects: true }
  ) as string[];

  return (
    <div className="rounded-xl border border-accent/20 bg-accent-tint">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-accent-text">
          <Info size={15} className="shrink-0" />
          {t("scheduler.settings.zoomAI.title")}
        </span>
        {open ? (
          <ChevronUp size={15} className="shrink-0 text-accent-text" />
        ) : (
          <ChevronDown size={15} className="shrink-0 text-accent-text" />
        )}
      </button>

      {open && (
        <div className="border-t border-accent/20 px-4 pb-4 pt-3">
          <p className="mb-3 text-xs text-accent-text">
            {t("scheduler.settings.zoomAI.description")}
          </p>

          <ul className="flex flex-col gap-2">
            {Array.isArray(requirements) &&
              requirements.map((_, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-accent-on-tint">
                  <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-accent-text" />
                  <span>
                    <Trans
                      i18nKey={`scheduler.settings.zoomAI.requirements.${i}`}
                      components={{ strong: <strong /> }}
                    />
                  </span>
                </li>
              ))}
          </ul>

          <div className="mt-4 flex flex-wrap gap-3">
            <a
              href={ZOOM_AI_ADMIN_URL}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-medium text-accent-text underline text-accent-text"
            >
              {t("scheduler.settings.zoomAI.linkAdmin")}
            </a>
            <a
              href={ZOOM_AI_COMPANION_URL}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-medium text-accent-text underline text-accent-text"
            >
              {t("scheduler.settings.zoomAI.linkHelp")}
            </a>
          </div>

          <p className="mt-3 text-xs text-accent-text">
            {t("scheduler.settings.zoomAI.outcome")}
          </p>
        </div>
      )}
    </div>
  );
}
